/**
 * LinkedIn Error Response Parser (Story 10.2, Task 1.10)
 *
 * Maps LinkedIn-specific error format to AdapterErrorCode.
 *
 * LinkedIn error response shape:
 * { "status": 403, "serviceErrorCode": 100, "code": "ACCESS_DENIED", "message": "..." }
 *
 * Special cases:
 * - serviceErrorCode 65604: revoked token → token_invalid (not just token_expired)
 * - HTTP 404 with "API version" or "sunset": provider_unavailable (version issue)
 * - status 429 with Retry-After: rate_limited with retryAfterMs
 */

import type { AdapterErrorCode } from "../adapter";

// ============================================================================
// Types
// ============================================================================

interface LinkedInErrorResponse {
  status?: number;
  serviceErrorCode?: number;
  code?: string;
  message?: string;
}

export interface ParsedLinkedInError {
  code: AdapterErrorCode;
  message: string;
  retryable: boolean;
  retryAfterMs?: number;
}

// ============================================================================
// Error Parser
// ============================================================================

/**
 * Parses a LinkedIn error response body (as JSON string) into a typed error.
 *
 * Returns null if the body cannot be parsed as a LinkedIn error,
 * allowing the caller to fall back to safeFetch default HTTP mapping.
 *
 * @param responseBody - Raw response body string (JSON)
 * @param retryAfterHeader - Value of Retry-After header (seconds), if present
 */
export function parseLinkedInError(
  responseBody: string,
  retryAfterHeader?: string | null
): ParsedLinkedInError | null {
  let parsed: LinkedInErrorResponse;
  try {
    parsed = JSON.parse(responseBody) as LinkedInErrorResponse;
  } catch {
    return null;
  }

  if (!parsed || typeof parsed !== "object") return null;
  if (!parsed.status && !parsed.code && !parsed.serviceErrorCode) return null;

  const status = parsed.status ?? 0;
  const serviceErrorCode = parsed.serviceErrorCode;
  const errorCode = parsed.code;
  const message = parsed.message ?? "Unknown LinkedIn API error";

  // Revoked token (serviceErrorCode 65604)
  if (serviceErrorCode === 65604) {
    return {
      code: "token_invalid",
      message,
      retryable: false,
    };
  }

  // 401 or UNAUTHORIZED → token_expired
  if (status === 401 || errorCode === "UNAUTHORIZED") {
    return {
      code: "token_expired",
      message,
      retryable: false,
    };
  }

  // 403 or ACCESS_DENIED → permission_denied
  if (status === 403 || errorCode === "ACCESS_DENIED") {
    return {
      code: "permission_denied",
      message,
      retryable: false,
    };
  }

  // 429 or THROTTLED → rate_limited
  if (status === 429 || errorCode === "THROTTLED") {
    let retryAfterMs: number | undefined;
    if (retryAfterHeader) {
      const seconds = Number.parseInt(retryAfterHeader, 10);
      if (!Number.isNaN(seconds) && seconds > 0) {
        retryAfterMs = seconds * 1000;
      }
    }
    return {
      code: "rate_limited",
      message,
      retryable: true,
      retryAfterMs,
    };
  }

  // 404 with API version / sunset keywords → provider_unavailable
  if (status === 404) {
    const lowerMessage = message.toLowerCase();
    if (lowerMessage.includes("api version") || lowerMessage.includes("sunset")) {
      return {
        code: "provider_unavailable",
        message: `LinkedIn API version issue: ${message}. Update LINKEDIN_API_VERSION.`,
        retryable: false,
      };
    }
    return {
      code: "account_not_found",
      message,
      retryable: false,
    };
  }

  // 5xx → provider_unavailable
  if (status >= 500) {
    return {
      code: "provider_unavailable",
      message,
      retryable: true,
    };
  }

  // Unrecognized error — return null to let safeFetch handle it
  return null;
}
